import { useEffect } from "react";

/**
 * Lightweight SEO hook — updates document.title, meta description,
 * og:title / og:description / og:url / og:image, and canonical link.
 * No extra dependencies needed.
 */
export function useSEO({ title, description, image, type = "website", noindex = false }) {
    useEffect(() => {
        if (!title && !description) return;
        const fullTitle = title ? `${title} | BM Juridik` : "BM Juridik – Juristbyrå i Sverige";
        document.title = fullTitle;

        const ensureMeta = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                Object.entries(attrs.key).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute("content", attrs.content);
        };

        // Robots noindex (for internal pages like /resurser)
        ensureMeta('meta[name="robots"]', {
            key: { name: "robots" },
            content: noindex ? "noindex, nofollow" : "index, follow",
        });

        if (description) {
            ensureMeta('meta[name="description"]', {
                key: { name: "description" },
                content: description,
            });
            ensureMeta('meta[property="og:description"]', {
                key: { property: "og:description" },
                content: description,
            });
            ensureMeta('meta[name="twitter:description"]', {
                key: { name: "twitter:description" },
                content: description,
            });
        }
        ensureMeta('meta[property="og:title"]', {
            key: { property: "og:title" },
            content: fullTitle,
        });
        ensureMeta('meta[name="twitter:title"]', {
            key: { name: "twitter:title" },
            content: fullTitle,
        });
        ensureMeta('meta[property="og:type"]', {
            key: { property: "og:type" },
            content: type,
        });
        ensureMeta('meta[property="og:url"]', {
            key: { property: "og:url" },
            content: window.location.href,
        });
        if (image) {
            ensureMeta('meta[property="og:image"]', {
                key: { property: "og:image" },
                content: image,
            });
            ensureMeta('meta[name="twitter:image"]', {
                key: { name: "twitter:image" },
                content: image,
            });
            ensureMeta('meta[name="twitter:card"]', {
                key: { name: "twitter:card" },
                content: "summary_large_image",
            });
        }

        // Canonical
        let link = document.head.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.setAttribute("rel", "canonical");
            document.head.appendChild(link);
        }
        link.setAttribute("href", window.location.href);
    }, [title, description, image, type, noindex]);
}
